/**
 * JournalPage — Research & Development Journal for lukhas.dev
 *
 * Presents the LUKHAS morning digest as an editorial research note.
 * Route: /journal
 */

import { useEffect, useState } from 'react'
import { Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions, Footer, Button, GlassCard } from '@lukhas/ui'
import {
  BookOpen,
  Activity,
  GitPullRequest,
  TrendingUp,
  AlertTriangle,
  Target,
  Zap,
  RefreshCw,
  Calendar,
  ChevronRight,
  Sparkles,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types & parser (mirrors lukhas.team/MorningDigestCard logic)
// ---------------------------------------------------------------------------

interface DigestSection {
  heading: string
  body: string
}

interface ParsedDigest {
  date: string
  headline: string
  subtitle: string
  quote: string
  sections: DigestSection[]
  footer: string
}

function parseDigestMarkdown(md: string): ParsedDigest {
  const lines = md.split('\n')
  let date = ''
  let headline = ''
  let subtitle = ''
  let quote = ''
  let footer = ''
  const sections: DigestSection[] = []
  let currentSection: DigestSection | null = null
  let bodyLines: string[] = []
  let fenceCount = 0
  let inFrontmatter = false
  let frontmatterDone = false

  const dateMatch = md.match(/^date:\s*(.+)$/m)
  if (dateMatch) date = dateMatch[1].trim()

  for (const line of lines) {
    if (line.trim() === '---') {
      fenceCount++
      if (fenceCount === 1) { inFrontmatter = true; continue }
      if (fenceCount === 2) { inFrontmatter = false; frontmatterDone = true; continue }
    }
    if (inFrontmatter || !frontmatterDone) continue
    if (line.startsWith('# ')) { headline = line.replace(/^# /, '').trim(); continue }
    if (!subtitle && line.match(/^\*[^*]/) && line.endsWith('*')) { subtitle = line.replace(/^\*|\*$/g, '').trim(); continue }
    if (line.startsWith('> ')) { quote = line.replace(/^> /, '').replace(/\*\*/g, '').trim(); continue }
    if (line.startsWith('*Generated by')) { footer = line.replace(/^\*|\*$/g, '').trim(); continue }
    if (line.startsWith('## ')) {
      if (currentSection) { currentSection.body = bodyLines.join('\n').trim(); sections.push(currentSection) }
      currentSection = { heading: line.replace(/^## /, '').trim(), body: '' }
      bodyLines = []
      continue
    }
    if (currentSection) bodyLines.push(line)
  }
  if (currentSection) { currentSection.body = bodyLines.join('\n').trim(); sections.push(currentSection) }

  return { date, headline, subtitle, quote, sections, footer }
}

// ---------------------------------------------------------------------------
// Section config
// ---------------------------------------------------------------------------

const SECTION_CONFIG: Record<string, { icon: typeof Activity; accent: string }> = {
  'system health':      { icon: Activity, accent: 'text-emerald-400' },
  "what's new":        { icon: Sparkles, accent: 'text-code-cyan' },
  "yesterday's":       { icon: GitPullRequest, accent: 'text-blue-400' },
  'active work':        { icon: GitPullRequest, accent: 'text-purple-400' },
  'test & coverage':    { icon: TrendingUp, accent: 'text-code-cyan' },
  'open issues':        { icon: AlertTriangle, accent: 'text-amber-400' },
  'lukhas_gap':         { icon: Zap, accent: 'text-orange-400' },
  "today's focus":      { icon: Target, accent: 'text-code-cyan' },
  'closing':            { icon: BookOpen, accent: 'text-awareness-silver' },
}

function sectionConfig(heading: string) {
  const key = heading.toLowerCase()
  for (const [k, cfg] of Object.entries(SECTION_CONFIG)) {
    if (key.includes(k)) return cfg
  }
  return { icon: BookOpen, accent: 'text-awareness-silver' }
}

// ---------------------------------------------------------------------------
// Markdown section body renderer
// ---------------------------------------------------------------------------

function SectionBody({ body }: { body: string }) {
  if (!body) return null
  const lines = body.split('\n').filter(l => l.trim())

  // Table
  const tableLines = lines.filter(l => l.includes('|'))
  if (tableLines.length >= 3) {
    const rows = tableLines
      .filter(l => !l.match(/^\|[-: |]+\|$/))
      .map(l => l.split('|').filter((_, i, a) => i > 0 && i < a.length - 1).map(c => c.trim()))
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-code-cyan/20">
              {rows[0]?.map((cell, i) => (
                <th key={i} className="text-left py-2 pr-6 text-code-cyan text-xs uppercase tracking-wide font-medium">{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(1).map((row, ri) => (
              <tr key={ri} className="border-b border-white/5">
                {row.map((cell, ci) => (
                  <td key={ci} className={`py-2 pr-6 ${ci === 0 ? 'text-awareness-silver' : 'text-awareness-silver/60'}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Numbered or bulleted list
  if (lines.some(l => l.match(/^[-*•]|^\d+\./))) {
    return (
      <ul className="space-y-2">
        {lines.map((line, i) => {
          const clean = line.replace(/^[-*•]\s*|^\d+\.\s*/, '')
          const boldMatch = clean.match(/^\*\*(.*?)\*\*:?\s*(.*)/)
          return (
            <li key={i} className="flex items-start gap-2 text-sm text-awareness-silver/70">
              <ChevronRight className="w-4 h-4 text-code-cyan shrink-0 mt-0.5" />
              {boldMatch
                ? <span><strong className="text-awareness-silver">{boldMatch[1]}:</strong> {boldMatch[2]}</span>
                : <span>{clean.replace(/\*\*(.*?)\*\*/g, '$1')}</span>
              }
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <p className="text-sm text-awareness-silver/70 leading-relaxed">
      {lines.join(' ').replace(/\*\*(.*?)\*\*/g, '$1')}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Digest section card
// ---------------------------------------------------------------------------

function DigestSectionCard({ section }: { section: DigestSection }) {
  const { icon: Icon, accent } = sectionConfig(section.heading)
  const isReflection = section.heading.toLowerCase().includes('reflection')

  return (
    <GlassCard className={`border-code-cyan/10 ${isReflection ? 'bg-code-gradient/5' : ''}`}>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.5} />
          <h3 className="text-sm font-medium tracking-wide text-awareness-silver">{section.heading}</h3>
        </div>
        {isReflection
          ? <p className="text-sm text-awareness-silver/60 italic leading-relaxed">{section.body.replace(/\*\*(.*?)\*\*/g, '$1')}</p>
          : <SectionBody body={section.body} />
        }
      </div>
    </GlassCard>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SERVE_API = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export default function JournalPage() {
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDigest = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${SERVE_API}/api/digest/morning/today/markdown`, { cache: 'no-store' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setMarkdown(await res.text())
    } catch {
      // Fallback: static file from docs/
      try {
        const today = new Date().toISOString().slice(0, 10)
        const year = today.slice(0, 4)
        const res = await fetch(`/docs/digests/${year}/${today}.md`, { cache: 'no-store' })
        if (res.ok) {
          setMarkdown(await res.text())
        } else {
          setError('No digest available yet for today — check back after 08:00 CET')
        }
      } catch {
        setError('Digest service unreachable')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchDigest() }, [])

  const digest = markdown ? parseDigestMarkdown(markdown) : null

  return (
    <div className="min-h-screen bg-consciousness-deep">
      {/* Header */}
      <Header className="fixed top-0 left-0 right-0 z-50 bg-consciousness-deep/80 backdrop-blur-md border-b border-code-cyan/20">
        <HeaderLogo href="/">
          <span className="text-2xl font-light tracking-[0.15em] text-awareness-silver">
            LUKHAS<span className="text-code-cyan">.DEV</span>
          </span>
        </HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
          <HeaderNavLink href="/community">Community</HeaderNavLink>
          <HeaderNavLink href="/journal">Journal</HeaderNavLink>
          <HeaderNavLink href="/playground">Playground</HeaderNavLink>
          <HeaderNavLink href="https://lukhas.com">Enterprise</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <a href="https://lukhas.id/login" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost">Sign In</Button>
          </a>
          <Button className="bg-code-gradient text-white">Get API Key</Button>
        </HeaderActions>
      </Header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em] text-code-cyan">Research Journal</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-[0.1em] mb-4 text-awareness-silver">
            Morning <span className="text-transparent bg-clip-text bg-code-gradient">Briefing</span>
          </h1>
          <p className="text-lg text-awareness-silver/70 max-w-2xl">
            Daily research notes from the LUKHAS AI Constellation — system health, what shipped, what's next, and the gaps we're closing.
          </p>
        </div>
      </section>

      {/* Digest content */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {loading && (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <GlassCard key={i} className="border-code-cyan/10 animate-pulse">
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-code-cyan/10 rounded w-32" />
                    <div className="h-3 bg-white/5 rounded w-full" />
                    <div className="h-3 bg-white/5 rounded w-4/5" />
                  </div>
                </GlassCard>
              ))}
            </div>
          )}

          {error && !loading && (
            <GlassCard className="border-amber-500/20">
              <div className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-awareness-silver/70 mb-4">{error}</p>
                <Button onClick={fetchDigest} variant="ghost" className="text-code-cyan">
                  <RefreshCw className="w-4 h-4 mr-2" /> Retry
                </Button>
              </div>
            </GlassCard>
          )}

          {digest && !loading && (
            <>
              {/* Article header */}
              <div className="mb-8 pb-8 border-b border-code-cyan/10">
                <div className="flex items-center gap-3 mb-4 text-sm text-awareness-silver/50">
                  <Calendar className="w-4 h-4 text-code-cyan" strokeWidth={1.5} />
                  <span>{digest.date || new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  <span className="text-code-cyan">·</span>
                  <span>Auto-generated · gpt-4o</span>
                  <button onClick={fetchDigest} className="ml-auto text-code-cyan/50 hover:text-code-cyan transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>

                <h2 className="text-3xl md:text-4xl font-light tracking-wide text-awareness-silver mb-2">
                  {digest.headline}
                </h2>
                {digest.subtitle && (
                  <p className="text-awareness-silver/60 italic text-lg">{digest.subtitle}</p>
                )}
                {digest.quote && (
                  <blockquote className="mt-4 pl-4 border-l-2 border-code-cyan/40 text-awareness-silver/50 italic">
                    "{digest.quote}"
                  </blockquote>
                )}
              </div>

              {/* Sections grid */}
              <div className="space-y-4">
                {digest.sections.map((section, i) => (
                  <DigestSectionCard key={i} section={section} />
                ))}
              </div>

              {/* Footer */}
              {digest.footer && (
                <p className="mt-8 text-xs text-awareness-silver/30 text-center">{digest.footer}</p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Archive teaser */}
      <section className="py-16 px-6 bg-consciousness-deep/60 border-t border-code-cyan/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h3 className="text-lg font-light text-awareness-silver mb-1">Previous Briefings</h3>
            <p className="text-sm text-awareness-silver/50">Access the full digest archive on GitHub</p>
          </div>
          <a
            href="https://github.com/LukhasAI/Lukhas/tree/main/docs/digests/2026"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost" className="text-code-cyan border border-code-cyan/30">
              View Archive <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <Footer activeDomain="lukhas.dev">
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Developer Resources</h3>
          <ul className="space-y-2">
            <li><a href="/docs" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Documentation</a></li>
            <li><a href="/journal" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Research Journal</a></li>
            <li><a href="/playground" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Playground</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Community</h3>
          <ul className="space-y-2">
            <li><a href="/community" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Governance</a></li>
            <li><a href="https://github.com/LukhasAI/Lukhas/blob/main/CONTRIBUTING.md" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Contributing</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Legal</h3>
          <ul className="space-y-2">
            <li><a href="/privacy" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Privacy</a></li>
            <li><a href="/terms" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Terms</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Other Platforms</h3>
          <ul className="space-y-2">
            <li><a href="https://lukhas.ai" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Main Platform</a></li>
            <li><a href="https://lukhas.com" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Enterprise</a></li>
          </ul>
        </div>
      </Footer>
    </div>
  )
}
